/**
 * Inventory Validation System for PLP and PDP
 * Checks current cart quantities against available inventory before allowing new items to be added
 */

// Guard against duplicate declarations when script is included multiple times
if (typeof window.__INVENTORY_VALIDATOR_LOADED === 'undefined') {
    window.__INVENTORY_VALIDATOR_LOADED = true;
    
    if (typeof InventoryValidator === 'undefined') {
        class InventoryValidator {
    constructor() {
        this.API_URL = window.EveryTable?.api?.baseUrl || "https://admin-app.everytable-sh.com/api";
        this.inventoryData = null;
        this.pendingAdds = {};
        this._cartSnapshot = null;
        this._cartSnapshotAt = 0;
        this._cartSnapshotPromise = null;
        this._validationTimer = null;
        this._pendingVariantIds = new Set();
        this._runFullValidation = false;
        this._inventoryPrefetchPromises = {};
        this._cartAdjustInFlight = false;
        this._lastAdjustmentNoticeKey = '';
        this._lastAdjustmentNoticeAt = 0;
    }

    normalizeLocationId(locationId) {
        if (window.EtInventoryDomain && typeof window.EtInventoryDomain.normalizeLocationId === 'function') {
            return window.EtInventoryDomain.normalizeLocationId(locationId);
        }
        const raw = (locationId || '').toString().trim();
        if (!raw) return '';
        if (raw.includes('gid://shopify/Location/')) {
            return raw.split('/').pop() || raw;
        }
        return raw;
    }

    normalizeProductId(productId) {
        if (window.EtInventoryDomain && typeof window.EtInventoryDomain.normalizeProductId === 'function') {
            return window.EtInventoryDomain.normalizeProductId(productId);
        }
        const raw = (productId || '').toString().trim();
        if (!raw) return '';
        if (raw.includes('gid://shopify/Product/')) {
            return raw.split('/').pop() || raw;
        }
        return raw;
    }

    // Check if today's date is selected
    isTodaySelected() {
        const fulfillmentConstants = window.FulfillmentConstants;
        const dateKey = fulfillmentConstants && fulfillmentConstants.DATE ? fulfillmentConstants.DATE : 'selected_fulfillment_date';
        const storageDate = localStorage.getItem(dateKey);
        return !!(storageDate && window.FulfillmentDateUtils?.isSelectedDateToday?.(storageDate));
    }

    isInventoryExemptProduct(product) {
        if (!product) {
            return false;
        }
        if (window.EtInventoryDomain && typeof window.EtInventoryDomain.isPayItForwardProduct === 'function') {
            if (window.EtInventoryDomain.isPayItForwardProduct(product)) {
                return true;
            }
        }
        if (product.gift_card === true || product.giftCard === true || product.is_gift_card === true) {
            return true;
        }
        return false;
    }

    isInventoryExemptVariant(variantId, options = {}) {
        const normalizedVariantId = String(variantId || '').trim();
        if (!normalizedVariantId) {
            return false;
        }

        const directButton = options.button;
        if (directButton && directButton.dataset) {
            if (directButton.dataset.isGiftCard === 'true') {
                return true;
            }
        }

        if (options.productId && this.inventoryData && this.inventoryData.products) {
            const normalizedProductId = this.normalizeProductId(options.productId);
            const product = this.inventoryData.products[normalizedProductId];
            if (this.isInventoryExemptProduct(product)) {
                return true;
            }
        }

        const escapedId = (window.EtInventoryDomain && window.EtInventoryDomain.escapeForSelector)
            ? window.EtInventoryDomain.escapeForSelector(normalizedVariantId)
            : normalizedVariantId;

        const submitButton = document.querySelector(`.product-form__submit[data-variant-id="${escapedId}"]`);
        if (submitButton && submitButton.getAttribute('data-is-gift-card') === 'true') {
            return true;
        }

        const variantInput = document.querySelector(`form input[name="id"][value="${escapedId}"]`);
        if (variantInput) {
            const form = variantInput.closest('form');
            const formButton = form ? form.querySelector('.product-form__submit[data-is-gift-card="true"]') : null;
            if (formButton) {
                return true;
            }
        }
        return false;
    }

    // Get selected location ID
    getSelectedLocationId() {
        const fulfillmentConstants = window.FulfillmentConstants;
        const locationKey = fulfillmentConstants && fulfillmentConstants.LOCATION_ID ? fulfillmentConstants.LOCATION_ID : 'selected_location_id';
        const locationId = localStorage.getItem(locationKey);
        return this.normalizeLocationId(locationId);
    }

    getCachedInventoryData(locationId = null, maxAgeMs = 15 * 60 * 1000) {
        const locationIdToSearch = this.normalizeLocationId(locationId || this.getSelectedLocationId());
        if (!locationIdToSearch) {
            return null;
        }

        const cacheKey = `inventory_data_${locationIdToSearch}`;
        let cachedData = null;
        try {
            cachedData = sessionStorage.getItem(cacheKey);
        } catch (error) {
            return null;
        }
        if (!cachedData) {
            return null;
        }

        try {
            const parsed = JSON.parse(cachedData);
            const now = Date.now();
            const cacheAge = now - (parsed.timestamp || 0);
            if (cacheAge < maxAgeMs && parsed.locationId === locationIdToSearch) {
                return parsed.data || null;
            }
        } catch (error) {}

        return null;
    }

    ensureInventoryPrefetch(locationId = null) {
        const locationIdToSearch = this.normalizeLocationId(locationId || this.getSelectedLocationId());
        if (!locationIdToSearch) {
            return Promise.resolve(null);
        }

        const cached = this.getCachedInventoryData(locationIdToSearch);
        if (cached) {
            if (this.normalizeLocationId(this.getSelectedLocationId()) === locationIdToSearch) {
                this.inventoryData = cached;
            }
            return Promise.resolve(cached);
        }

        if (this._inventoryPrefetchPromises[locationIdToSearch]) {
            return this._inventoryPrefetchPromises[locationIdToSearch];
        }

        this._inventoryPrefetchPromises[locationIdToSearch] = this.fetchInventoryData(locationIdToSearch)
            .then((data) => {
                if (data) {
                    if (this.normalizeLocationId(this.getSelectedLocationId()) === locationIdToSearch) {
                        this.inventoryData = data;
                    }
                }
                return data;
            })
            .finally(() => {
                delete this._inventoryPrefetchPromises[locationIdToSearch];
            });

        return this._inventoryPrefetchPromises[locationIdToSearch];
    }

    getCartFromWatcher() {
        try {
            if (window.cartWatcher && typeof window.cartWatcher.storedCart === 'function') {
                const cart = window.cartWatcher.storedCart();
                if (cart && Array.isArray(cart.items)) {
                    return cart;
                }
            }
        } catch (error) {}
        return null;
    }

    // Fetch inventory data with caching
    async fetchInventoryData(locationId = null) {
        const locationIdToSearch = locationId || this.getSelectedLocationId();
        if (!locationIdToSearch) return null;

        const normalizedLocationId = this.normalizeLocationId(locationIdToSearch);
        if (!normalizedLocationId) return null;

        const cacheKey = `inventory_data_${normalizedLocationId}`;

        if (window.EtInventoryDomain && typeof window.EtInventoryDomain.fetchInventoryForLocation === 'function') {
            try {
                const data = await window.EtInventoryDomain.fetchInventoryForLocation(normalizedLocationId);
                try {
                    sessionStorage.setItem(
                        cacheKey,
                        JSON.stringify({
                            data: data,
                            timestamp: Date.now(),
                            locationId: normalizedLocationId
                        })
                    );
                } catch (storageErr) {
                    if (typeof console !== 'undefined' && console.warn) {
                        console.warn('InventoryValidator: sessionStorage cache write failed', storageErr);
                    }
                }
                return data;
            } catch (error) {
                console.error('InventoryValidator: Error fetching inventory data:', error);
                /* Fall through to legacy HTTP fetch */
            }
        }

        try {
            const cachedData = sessionStorage.getItem(cacheKey);
            if (cachedData) {
                const parsed = JSON.parse(cachedData);
                if (parsed.timestamp && (Date.now() - parsed.timestamp) < 15 * 60 * 1000) {
                    return parsed.data;
                }
            }
        } catch (e) {}

        try {
            const response = await fetch(`${this.API_URL}/location/${normalizedLocationId}/inventory`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            try {
                sessionStorage.setItem(cacheKey, JSON.stringify({ data: data, timestamp: Date.now(), locationId: normalizedLocationId }));
            } catch (storageErr) {
                if (typeof console !== 'undefined' && console.warn) console.warn('InventoryValidator: sessionStorage cache write failed', storageErr);
            }
            return data;
        } catch (error) {
            console.error('InventoryValidator: Error fetching inventory data:', error);
            return null;
        }
    }

    // Find inventory info for a specific variant
    findVariantInventory(variantId, fallbackProductId = null) {
        if (!this.inventoryData || !this.inventoryData.products) {
            return null;
        }
        const normalizedVariantId = String(variantId);
        const normalizedFallbackProductId = this.normalizeProductId(fallbackProductId);

        for (const productId in this.inventoryData.products) {
            const product = this.inventoryData.products[productId];
            
            // Check if this is a pay-it-forward product (by collection)
            const isPayItForward = product.collections?.some((collection) =>
                collection.handle === 'pay-it-forward-collection' ||
                collection.handle === 'pay-it-forward'
            );
            
            if (isPayItForward) {
                let belongsToThisProduct = false;
                
                if (product.variants?.[normalizedVariantId]) {
                    belongsToThisProduct = true;
                }
                
                if (this.isBundleProduct(normalizedVariantId)) {
                    const bundleProductId = this.getProductIdFromVariantId(normalizedVariantId);
                    if (bundleProductId === productId) {
                        belongsToThisProduct = true;
                    }
                }
                
                if (belongsToThisProduct) {
                    return {
                        available: 999,
                        committed: 0,
                        incoming: 0
                    };
                }
            }
        }

        const isBundle = this.isBundleProduct(normalizedVariantId);
        if (isBundle) {
            return this.getBundleInventory(normalizedVariantId);
        }

        // Find the product that contains this specific variant
        for (const productId in this.inventoryData.products) {
            const product = this.inventoryData.products[productId];
            
            if (!product.variants) continue;
            
            // Check if this product contains the variant we're looking for
            if (product.variants[normalizedVariantId]) {
                const resolved = this.findToGoInventoryForProduct(product);
                if (resolved) {
                    return resolved;
                } 
            }
        }

        // Fallback: resolve by product context from rendered card/form when variant IDs differ
        // between storefront payloads and inventory payloads.
        if (normalizedFallbackProductId && this.inventoryData.products[normalizedFallbackProductId]) {
            const fallbackProduct = this.inventoryData.products[normalizedFallbackProductId];
            const isPayItForwardFallback = fallbackProduct.collections?.some((collection) =>
                collection.handle === 'pay-it-forward-collection' ||
                collection.handle === 'pay-it-forward'
            );
            if (isPayItForwardFallback) {
                return {
                    available: 999,
                    committed: 0,
                    incoming: 0
                };
            }
            const resolvedFromFallbackProduct = this.findToGoInventoryForProduct(fallbackProduct);
            if (resolvedFromFallbackProduct) {
                return resolvedFromFallbackProduct;
            }
        }

        const contextProductId = this.getProductIdFromVariantContext(normalizedVariantId);
        if (contextProductId && this.inventoryData.products[contextProductId]) {
            const contextProduct = this.inventoryData.products[contextProductId];
            const isPayItForwardContext = contextProduct.collections?.some((collection) =>
                collection.handle === 'pay-it-forward-collection' ||
                collection.handle === 'pay-it-forward'
            );
            if (isPayItForwardContext) {
                return {
                    available: 999,
                    committed: 0,
                    incoming: 0
                };
            }
            const resolvedFromContext = this.findToGoInventoryForProduct(contextProduct);
            if (resolvedFromContext) {
                return resolvedFromContext;
            }
        }
        return null;
    }

    findToGoInventoryForProduct(product) {
        if (window.EtInventoryDomain && typeof window.EtInventoryDomain.getToGoInventoryForProduct === 'function') {
            return window.EtInventoryDomain.getToGoInventoryForProduct(product);
        }
        if (!product || !product.variants) {
            return null;
        }
        for (const variantIdKey in product.variants) {
            const variant = product.variants[variantIdKey];
            if (variant && variant.inventory) {
                return {
                    available: variant.inventory.available || 0,
                    committed: 0,
                    incoming: variant.inventory.incoming || 0
                };
            }
        }
        return null;
    }

    getProductIdFromVariantContext(variantId) {
        const escapedId = (window.EtInventoryDomain && window.EtInventoryDomain.escapeForSelector)
            ? window.EtInventoryDomain.escapeForSelector(String(variantId))
            : String(variantId);
        const card = document.querySelector(`.product-card-wrapper[data-variant-id="${escapedId}"], .recharge-card[data-variant-id="${escapedId}"]`);
        const cardProductId = card && card.getAttribute('data-product-id');
        if (cardProductId) {
            return this.normalizeProductId(cardProductId);
        }

        const variantInput = document.querySelector(`form input[name="id"][value="${escapedId}"]`);
        if (variantInput) {
            const form = variantInput.closest('form');
            const productNode = form && form.closest('[data-product-id]');
            const formProductId = productNode && productNode.getAttribute('data-product-id');
            if (formProductId) {
                return this.normalizeProductId(formProductId);
            }
        }

        return null;
    }

    // Check if a variant belongs to a bundle product
    isBundleProduct(variantId) {
        const escapedId = (window.EtInventoryDomain && window.EtInventoryDomain.escapeForSelector)
            ? window.EtInventoryDomain.escapeForSelector(String(variantId))
            : String(variantId);
        const bundleElements = document.querySelectorAll(`[data-variant-id="${escapedId}"][data-is-bundle="true"]`);
        return bundleElements.length > 0;
    }

    // Get inventory for a bundle based on its components
    getBundleInventory(variantId) {
        const productId = this.getProductIdFromVariantId(variantId);
        if (!productId || !this.inventoryData || !this.inventoryData.products) {
            return null;
        }

        if (window.EtInventoryDomain && typeof window.EtInventoryDomain.getBundleInventory === 'function') {
            return window.EtInventoryDomain.getBundleInventory(this.inventoryData.products, productId);
        }

        const product = this.inventoryData.products[productId];
        if (!product) {
            return null;
        }

        if (!product.bundleComponents || !Array.isArray(product.bundleComponents) || product.bundleComponents.length === 0) {
            return {
                available: 0,
                committed: 0,
                incoming: 0
            };
        }

        const bundleComponents = product.bundleComponents;
        let minAvailable = Infinity;
        let totalIncoming = 0;

        for (const component of bundleComponents) {
            const componentProductId = component.product_id;
            const componentQuantity = component.quantity || 1;

            const componentInventory = this.findComponentInventory(componentProductId);
            if (componentInventory) {
                const availableForBundle = Math.floor(componentInventory.available / componentQuantity);
                minAvailable = Math.min(minAvailable, availableForBundle);
                totalIncoming += componentInventory.incoming || 0;
            } else {
                minAvailable = 0;
            }
        }

        if (minAvailable === Infinity) {
            minAvailable = 0;
        }

        return {
            available: minAvailable,
            committed: 0,
            incoming: totalIncoming
        };
    }

    getProductIdFromVariantId(variantId) {
        if (!this.inventoryData || !this.inventoryData.products) {
            return null;
        }

        const escapedId = (window.EtInventoryDomain && window.EtInventoryDomain.escapeForSelector)
            ? window.EtInventoryDomain.escapeForSelector(String(variantId))
            : String(variantId);
        const bundleElement = document.querySelector(`[data-variant-id="${escapedId}"][data-is-bundle="true"]`);
        if (bundleElement) {
            const productId = bundleElement.getAttribute('data-product-id');
            if (productId) {
                return productId;
            }
        }

        const norm = (window.EtInventoryDomain && typeof window.EtInventoryDomain.normalizeEntityId === 'function')
            ? window.EtInventoryDomain.normalizeEntityId
            : function (id) { return (id == null ? '' : String(id)).trim(); };
        const normalizedVariantId = norm(variantId);

        for (const productId in this.inventoryData.products) {
            const product = this.inventoryData.products[productId];
            if (!product.variants) continue;
            if (product.variants[variantId] || product.variants[normalizedVariantId]) {
                return productId;
            }
            for (const key in product.variants) {
                if (norm(key) === normalizedVariantId) return productId;
            }
        }

        return null;
    }

    findComponentInventory(productId) {
        if (!this.inventoryData || !this.inventoryData.products) {
            return null;
        }

        if (window.EtInventoryDomain && typeof window.EtInventoryDomain.findComponentInventory === 'function') {
            return window.EtInventoryDomain.findComponentInventory(this.inventoryData.products, productId);
        }

        const product = this.inventoryData.products[productId];
        if (!product || !product.variants) {
            return null;
        }

        for (const variantId in product.variants) {
            const variant = product.variants[variantId];
            if (variant && variant.inventory) {
                return {
                    available: variant.inventory.available || 0,
                    committed: 0,
                    incoming: variant.inventory.incoming || 0
                };
            }
        }

        return null;
    }

    async fetchCartSnapshot(maxAgeMs = 1200, options = {}) {
        const preferNetwork = !!options.preferNetwork;
        const now = Date.now();
        if (this._cartSnapshotPromise) {
            return this._cartSnapshotPromise;
        }

        if (!preferNetwork) {
            if (this._cartSnapshot && (maxAgeMs <= 0 || (now - this._cartSnapshotAt) < maxAgeMs)) {
                return this._cartSnapshot;
            }

            const watcherCart = this.getCartFromWatcher();
            if (watcherCart) {
                this._cartSnapshot = watcherCart;
                this._cartSnapshotAt = now;
                return this._cartSnapshot;
            }
        } else if (maxAgeMs > 0 && this._cartSnapshot && (now - this._cartSnapshotAt) < maxAgeMs) {
            return this._cartSnapshot;
        }

        this._cartSnapshotPromise = (async () => {
            const response = await fetch('/cart.js', {
                headers: { Accept: 'application/json' }
            });
            if (!response.ok) {
                throw new Error(`/cart.js failed (${response.status})`);
            }
            const bodyText = await response.text();
            let cart = null;
            try {
                cart = JSON.parse(bodyText);
            } catch (parseError) {
                throw new Error('/cart.js returned non-JSON response');
            }
            if (!cart || !Array.isArray(cart.items)) {
                throw new Error('/cart.js returned invalid cart payload');
            }
            this._cartSnapshot = cart;
            this._cartSnapshotAt = Date.now();
            return cart;
        })();

        try {
            return await this._cartSnapshotPromise;
        } catch (error) {
            // If /cart.js fails (429/non-JSON), fall back to the latest known cart.
            const fallback = this.getCartFromWatcher() || this._cartSnapshot;
            if (fallback && Array.isArray(fallback.items)) {
                return fallback;
            }
            throw error;
        } finally {
            this._cartSnapshotPromise = null;
        }
    }

    // Get current cart quantity for a specific variant (aggregates across all lines with matching variant_id)
    async getCurrentCartQuantity(variantId, cartSnapshot = null) {
        try {
            const cart = cartSnapshot || await this.fetchCartSnapshot();
            const variantIdStr = variantId.toString();
            const totalQuantity = (cart.items || [])
                .filter(item => item.variant_id.toString() === variantIdStr)
                .reduce((sum, item) => sum + (item.quantity || 0), 0);
            return totalQuantity;
        } catch (error) {
            console.error('🛒 InventoryValidator: Error getting cart quantity:', error);
            return 0;
        }
    }

    // Track a pending add operation
    trackPendingAdd(variantId, quantity = 1) {
        if (!this.pendingAdds[variantId]) {
            this.pendingAdds[variantId] = 0;
        }
        this.pendingAdds[variantId] += quantity;
    }

    // Clear pending add tracking for a variant
    clearPendingAdd(variantId) {
        if (this.pendingAdds[variantId]) {
            delete this.pendingAdds[variantId];
        }
    }

    // Reduce pending quantity (for partial completion)
    reducePendingAdd(variantId, quantity = 1) {
        if (this.pendingAdds[variantId]) {
            this.pendingAdds[variantId] -= quantity;
            if (this.pendingAdds[variantId] <= 0) {
                delete this.pendingAdds[variantId];
            }
        }
    }

    // Check if adding a quantity would exceed inventory
    async canAddToCart(variantId, quantityToAdd = 1, options = {}) {

        if (this.isInventoryExemptVariant(variantId, options)) {
            return { canAdd: true, reason: 'inventory_exempt' };
        }

        // Only check inventory if today is selected
        if (!this.isTodaySelected()) {
            return { canAdd: true, reason: 'not_today' };
        }

        const locationId = this.getSelectedLocationId();
        if (!locationId) {
            return { canAdd: true, reason: 'no_location' };
        }

        // Require warm inventory cache on add-to-cart to avoid fail-open oversells.
        this.inventoryData = options.inventoryData || this.getCachedInventoryData(locationId);
        if (!this.inventoryData) {
            this.ensureInventoryPrefetch(locationId)
                .then(() => { this.schedulePageValidation(100); })
                .catch(() => {});
            return { canAdd: false, reason: 'inventory_prefetch_pending' };
        }

        // Get current cart quantity
        const currentCartQuantity = await this.getCurrentCartQuantity(variantId, options.cartSnapshot || null);
        
        // Get available inventory
        const inventoryInfo = this.findVariantInventory(variantId, options.productId || null);
        if (!inventoryInfo) {
            return {
                canAdd: false,
                reason: 'no_inventory_info',
                currentCartQuantity: currentCartQuantity,
                quantityToAdd: quantityToAdd,
                totalRequested: currentCartQuantity + quantityToAdd,
                available: 0
            };
        }

        const availableQuantity = Math.max(0, Number(inventoryInfo.available) || 0);

        const pendingQuantity = this.pendingAdds[variantId] || 0;
        const effectiveRequestedAdd = pendingQuantity + quantityToAdd;
        const totalRequestedQuantity = currentCartQuantity + effectiveRequestedAdd;

        if (totalRequestedQuantity > availableQuantity) {
            return {
                canAdd: false,
                reason: 'exceeds_inventory',
                currentCartQuantity: currentCartQuantity,
                quantityToAdd: quantityToAdd,
                totalRequested: totalRequestedQuantity,
                available: availableQuantity,
                shortfall: totalRequestedQuantity - availableQuantity
            };
        }

        return {
            canAdd: true,
            reason: 'within_limits',
            currentCartQuantity: currentCartQuantity,
            quantityToAdd: quantityToAdd,
            totalRequested: totalRequestedQuantity,
            available: availableQuantity
        };
    }

    // Show inventory warning notification
    showInventoryWarning(variantId, checkResult) {
        const escapedId = (window.EtInventoryDomain && window.EtInventoryDomain.escapeForSelector)
            ? window.EtInventoryDomain.escapeForSelector(String(variantId))
            : String(variantId);
        // Remove any existing warnings for this variant
        const existingWarning = document.querySelector(`[data-inventory-warning="${escapedId}"]`);
        if (existingWarning) {
            existingWarning.remove();
        }

        // Create warning element
        const warning = document.createElement('div');
        warning.className = 'inventory-warning-notification';
        const safeVariantId = (typeof variantId === 'string' || typeof variantId === 'number') ? String(variantId) : '';
        warning.setAttribute('data-inventory-warning', safeVariantId);
        warning.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background-color: #f44336;
            color: white;
            padding: 15px 20px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            z-index: 10000;
            max-width: 400px;
            font-size: 14px;
            line-height: 1.4;
            animation: slideInRight 0.3s ease-out;
        `;

        const available = Math.max(0, Number(checkResult.available) || 0);
        const currentQty = Math.max(0, Number(checkResult.currentCartQuantity) || 0);
        const requestedQty = Math.max(0, Number(checkResult.quantityToAdd) || 0);
        const requestedTotal = Math.max(0, Number(checkResult.totalRequested) || (currentQty + requestedQty));
        const messageLine = currentQty > 0
            ? `You already have ${currentQty} in your cart.<br>Only ${available} available for today's delivery.`
            : `You're trying to add ${requestedQty || requestedTotal}, but only ${available} available for today's delivery.`;

        warning.innerHTML = `
            <div style="display: flex; align-items: flex-start; gap: 10px;">
                <div style="flex-shrink: 0; margin-top: 2px;">&#x26A0;&#xFE0F;</div>
                <div>
                    <strong>Inventory Limit Reached</strong><br>
                    ${messageLine}
                </div>
            </div>
        `;

        // Add to page
        document.body.appendChild(warning);

        // Auto-remove after 5 seconds
        setTimeout(() => {
            if (warning.parentNode) {
                warning.style.animation = 'slideOutRight 0.3s ease-in';
                setTimeout(() => {
                    if (warning.parentNode) {
                        warning.remove();
                    }
                }, 300);
            }
        }, 5000);
    }

    showInventoryLoadingWarning() {
        const existing = document.querySelector('[data-inventory-loading-warning="true"]');
        if (existing) {
            return;
        }

        const warning = document.createElement('div');
        warning.setAttribute('data-inventory-loading-warning', 'true');
        warning.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background-color: #ff9800;
            color: white;
            padding: 12px 16px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            z-index: 10000;
            max-width: 360px;
            font-size: 14px;
            line-height: 1.4;
            animation: slideInRight 0.3s ease-out;
        `;
        warning.textContent = 'Checking today\'s inventory. Please try again in a moment.';
        document.body.appendChild(warning);

        setTimeout(() => {
            if (warning.parentNode) {
                warning.style.animation = 'slideOutRight 0.3s ease-in';
                setTimeout(() => warning.remove(), 300);
            }
        }, 2500);
    }

    // Disable add to cart button
    disableAddToCartButton(button, variantId, disabledText = 'Inventory Limit Reached') {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.style.opacity = '0.5';
        button.style.cursor = 'not-allowed';
        
        // Add disabled class if it doesn't exist
        if (!button.classList.contains('inventory-disabled')) {
            button.classList.add('inventory-disabled');
        }

        const iconSpan = button.querySelector('.add-button__icon');
        if (!iconSpan) {
            const buttonText = button.querySelector('span');
            if (buttonText) {
                const originalText = buttonText.getAttribute('data-original-text') || buttonText.textContent;
                buttonText.setAttribute('data-original-text', originalText);
                buttonText.textContent = disabledText;
            } else {
                button.setAttribute('title', disabledText);
                button.setAttribute('aria-label', disabledText);
            }
        } else {
            button.setAttribute('title', disabledText);
        }

        if (button.classList.contains('recharge-card__add-btn')) {
            const rechargeCard = button.closest('.recharge-card');
            if (rechargeCard) {
                let stateLabel = rechargeCard.querySelector('.sold-out-label[data-inventory-validator-label="true"]') ||
                    rechargeCard.querySelector('.sold-out-label');
                if (!stateLabel) {
                    stateLabel = document.createElement('div');
                    stateLabel.className = 'sold-out-label';
                    stateLabel.setAttribute('data-inventory-validator-label', 'true');
                    stateLabel.style.cssText = `
                        position: absolute;
                        top: 10px;
                        right: 10px;
                        background: #a4cbc1;
                        color: #00464b;
                        padding: 4px 8px;
                        border-radius: 4px;
                        font-size: 11px;
                        font-weight: bold;
                        line-height: 1.2;
                        text-align: center;
                        z-index: 10;
                        max-width: 130px;
                    `;
                    rechargeCard.style.position = 'relative';
                    rechargeCard.appendChild(stateLabel);
                }
                stateLabel.textContent = 'Sold out';
            }
        } else {
            this.syncRegularCardSoldOutState(button, true);
        }
    }

    // Enable add to cart button
    enableAddToCartButton(button, variantId) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.style.opacity = '1';
        button.style.cursor = 'pointer';
        
        // Remove disabled class
        button.classList.remove('inventory-disabled');

        const iconSpan = button.querySelector('.add-button__icon');
        if (iconSpan) {
            button.removeAttribute('title');
        } else {
            const buttonText = button.querySelector('span');
            if (buttonText) {
                const originalText = buttonText.getAttribute('data-original-text');
                if (originalText) {
                    buttonText.textContent = originalText;
                    buttonText.removeAttribute('data-original-text');
                }
            } else {
                button.removeAttribute('title');
                button.removeAttribute('aria-label');
            }
        }

        if (button.classList.contains('recharge-card__add-btn')) {
            const rechargeCard = button.closest('.recharge-card');
            const stateLabel = rechargeCard ? rechargeCard.querySelector('.sold-out-label[data-inventory-validator-label="true"]') : null;
            if (stateLabel) {
                stateLabel.remove();
            }
        } else {
            this.syncRegularCardSoldOutState(button, false);
        }
    }

    syncRegularCardSoldOutState(button, isSoldOut) {
        const productCard = button ? button.closest('.product-card-wrapper') : null;
        if (!productCard) {
            return;
        }

        const tagsContainer = productCard.querySelector('.card_tags');
        if (!tagsContainer) {
            return;
        }
        
        if (isSoldOut) {
            // Prefer to re-use any existing "Sold out" tag instead of creating duplicates.
            // 1. Look for a validator-managed tag.
            let soldOutTag = tagsContainer.querySelector('.tag[data-inventory-validator-sold-out="true"]');
            // 2. Fall back to any existing sold-out tag created by other systems and "adopt" it.
            if (!soldOutTag) {
                soldOutTag = tagsContainer.querySelector('.tag.tag--sold-out, .tag[data-inventory-sold-out="true"]');
                if (soldOutTag) {
                    soldOutTag.setAttribute('data-inventory-validator-sold-out', 'true');
                }
            }

            const validatorTags = tagsContainer.querySelectorAll('.tag[data-inventory-validator-sold-out="true"]');
            if (validatorTags.length > 1) {
                // If previous buggy states created multiple validator tags, keep the first and remove the rest.
                validatorTags.forEach((tag, index) => {
                    if (index > 0) tag.remove();
                });
            }

            if (!soldOutTag) {
                soldOutTag = document.createElement('div');
                soldOutTag.className = 'tag tag--sold-out';
                soldOutTag.setAttribute('data-inventory-validator-sold-out', 'true');
                tagsContainer.appendChild(soldOutTag);
            }
            
            soldOutTag.textContent = 'Sold out';
            productCard.classList.add('sold-out');
            return;
        }
        
        const validatorTags = tagsContainer.querySelectorAll('.tag[data-inventory-validator-sold-out="true"]');
        validatorTags.forEach((tag) => tag.remove());
        
        if (!tagsContainer.querySelector('.tag[data-inventory-sold-out="true"]')) {
            productCard.classList.remove('sold-out');
        }
    }

    // Check and adjust cart quantities when switching to today
    async checkAndAdjustCartQuantities() {
        if (this._cartAdjustInFlight) {
            return;
        }

        // Only proceed if today is selected
        if (!this.isTodaySelected()) {
            return;
        }

        const locationId = this.getSelectedLocationId();
        if (!locationId) {
            return;
        }

        // Ensure inventory is warm for this run so first refresh can adjust immediately.
        this.inventoryData = this.getCachedInventoryData(locationId);
        if (!this.inventoryData) {
            try {
                this.inventoryData = await this.ensureInventoryPrefetch(locationId);
            } catch (prefetchError) {
                this.inventoryData = null;
            }
            if (!this.inventoryData) {
                return;
            }
        }

        this._cartAdjustInFlight = true;
        try {
            // Get current cart (prefer network to avoid duplicate adjustments from stale watcher snapshots)
            const cart = await this.fetchCartSnapshot(0, { preferNetwork: true });
            if (!cart || !Array.isArray(cart.items)) {
                return;
            }

            const adjustments = [];

            // Group cart items by variant_id and aggregate quantities (variants can span multiple lines)
            const variantGroups = new Map();
            for (const cartItem of cart.items) {
                const variantId = cartItem.variant_id.toString();
                if (!variantGroups.has(variantId)) {
                    variantGroups.set(variantId, { items: [], totalQuantity: 0, productId: cartItem.product_id ? String(cartItem.product_id) : null });
                }
                const group = variantGroups.get(variantId);
                group.items.push({ key: cartItem.key, quantity: cartItem.quantity });
                group.totalQuantity += cartItem.quantity || 0;
            }

            // Check each variant group against inventory
            for (const [variantId, group] of variantGroups) {
                const totalQuantity = group.totalQuantity;
                const productId = group.productId;
                const inventoryInfo = this.findVariantInventory(variantId, productId);
                if (!inventoryInfo) {
                    continue;
                }

                const availableQuantity = Math.max(0, Number(inventoryInfo.available) || 0);

                if (totalQuantity <= availableQuantity) {
                    continue;
                }

                const excess = totalQuantity - availableQuantity;
                let remainingToAssign = availableQuantity;

                for (const item of group.items) {
                    const newQuantity = Math.min(item.quantity, remainingToAssign);
                    remainingToAssign -= newQuantity;
                    if (newQuantity !== item.quantity) {
                        adjustments.push({
                            lineKey: item.key,
                            variantId: variantId,
                            currentQuantity: item.quantity,
                            newQuantity: newQuantity,
                            excess: item.quantity - newQuantity
                        });
                    }
                }
            }

            // If there are over-inventory items, proactively adjust the cart down to the allowed quantity.
            if (adjustments.length > 0) {
                for (const adj of adjustments) {
                    try {
                        await fetch('/cart/change.js', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Accept': 'application/json'
                            },
                            body: JSON.stringify({
                                id: adj.lineKey,
                                quantity: adj.newQuantity
                            })
                        });
                    } catch (changeError) {
                        console.error('InventoryValidator: Failed to adjust cart line', adj.lineKey, changeError);
                    }
                }

                // Refresh cart snapshot after adjustments so future checks use the corrected cart.
                try {
                    const adjustedCart = await this.fetchCartSnapshot(0, { preferNetwork: true });
                    if (adjustedCart && Array.isArray(adjustedCart.items)) {
                        this._cartSnapshot = adjustedCart;
                        this._cartSnapshotAt = Date.now();
                        if (window.cartWatcher && typeof window.cartWatcher.storeCart === 'function') {
                            try {
                                window.cartWatcher.storeCart(adjustedCart);
                            } catch (storeErr) {}
                        }
                        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
                            try {
                                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'inventory-validator', cartData: adjustedCart });
                            } catch (publishErr) {}
                        }
                        if (window.cartWatcher && typeof window.cartWatcher.emitCartChanges === 'function') {
                            try {
                                await window.cartWatcher.emitCartChanges();
                            } catch (emitErr) {}
                        }
                    }
                } catch (snapshotError) {}

                let dateKey = '';
                try {
                    dateKey = (window.FulfillmentConstants && window.FulfillmentConstants.DATE
                        ? localStorage.getItem(window.FulfillmentConstants.DATE)
                        : localStorage.getItem('selected_fulfillment_date')) || '';
                } catch (e) {}
                const keyPayload = adjustments
                    .map((adj) => `${adj.variantId}:${adj.currentQuantity}->${adj.newQuantity}`)
                    .sort()
                    .join('|');
                const adjustmentNoticeKey = `${locationId}|${dateKey}|${keyPayload}`;
                const now = Date.now();
                const isDuplicateNotice =
                    this._lastAdjustmentNoticeKey === adjustmentNoticeKey &&
                    (now - this._lastAdjustmentNoticeAt) < 5000;

                if (!isDuplicateNotice) {
                    this.showCartAdjustmentNotification(adjustments);
                    this._lastAdjustmentNoticeKey = adjustmentNoticeKey;
                    this._lastAdjustmentNoticeAt = now;
                }
            }
        } catch (error) {
            console.error('Error adjusting cart quantities:', error);
        } finally {
            this._cartAdjustInFlight = false;
        }
    }

    // Show notification about cart adjustments
    showCartAdjustmentNotification(adjustments) {
        // Remove any existing adjustment notifications
        const existingNotification = document.querySelector('[data-inventory-adjustment-notification]');
        if (existingNotification) {
            existingNotification.remove();
        }

        // Create notification element
        const notification = document.createElement('div');
        notification.className = 'inventory-adjustment-notification';
        notification.setAttribute('data-inventory-adjustment-notification', 'true');
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background-color: #ff9800;
            color: white;
            padding: 15px 20px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            z-index: 10000;
            max-width: 400px;
            font-size: 14px;
            line-height: 1.4;
            animation: slideInRight 0.3s ease-out;
        `;

        const totalExcess = adjustments.reduce((sum, adj) => sum + adj.excess, 0);
        
        notification.innerHTML = `
            <div style="display: flex; align-items: flex-start; gap: 10px;">
                <div style="flex-shrink: 0; margin-top: 2px;">⚠️</div>
                <div>
                    <strong>Inventory Limit Reached</strong><br>
                    ${totalExcess} item${totalExcess !== 1 ? 's' : ''} in your cart exceeded today's available inventory and were adjusted down to the available quantity for today. Please review your cart before checkout.
                </div>
            </div>
        `;

        // Add to page
        document.body.appendChild(notification);

        // Auto-remove after 8 seconds
        setTimeout(() => {
            if (notification.parentNode) {
                notification.style.animation = 'slideOutRight 0.3s ease-in';
                setTimeout(() => {
                    if (notification.parentNode) {
                        notification.remove();
                    }
                }, 300);
            }
        }, 8000);
    }

         // Update button state based on inventory check
     async updateButtonState(button, variantId, showNotification = false, options = {}) {
         
         const checkResult = await this.canAddToCart(variantId, 1, options);
         
         if (checkResult.canAdd) {
             this.enableAddToCartButton(button, variantId);
         } else {
             const isLoadingInventory = checkResult.reason === 'inventory_prefetch_pending';
             this.disableAddToCartButton(
                 button,
                 variantId,
                 isLoadingInventory ? 'Checking availability...' : 'Inventory Limit Reached'
             );
             // Only show notification if explicitly requested (for user attempts)
             if (showNotification && checkResult.reason === 'exceeds_inventory') {
                 this.showInventoryWarning(variantId, checkResult);
             } else if (showNotification && isLoadingInventory) {
                 this.showInventoryLoadingWarning();
             }
         }

         return checkResult;
     }

    extractVariantIdsFromEvent(event) {
        const ids = new Set();
        if (!event || typeof event !== 'object') {
            return [];
        }

        if (event.variantId !== undefined && event.variantId !== null && `${event.variantId}`.trim() !== '') {
            ids.add(`${event.variantId}`);
        }
        if (event.productVariantId !== undefined && event.productVariantId !== null && `${event.productVariantId}`.trim() !== '') {
            ids.add(`${event.productVariantId}`);
        }

        const cartData = event.cartData;
        if (cartData && typeof cartData === 'object') {
            if (cartData.variant_id !== undefined && cartData.variant_id !== null) {
                ids.add(`${cartData.variant_id}`);
            }
            if (cartData.item && cartData.item.variant_id !== undefined && cartData.item.variant_id !== null) {
                ids.add(`${cartData.item.variant_id}`);
            }
            if (Array.isArray(cartData.items)) {
                for (const item of cartData.items) {
                    if (item && item.variant_id !== undefined && item.variant_id !== null) {
                        ids.add(`${item.variant_id}`);
                    }
                }
            }
            if (Array.isArray(cartData.items_added)) {
                for (const item of cartData.items_added) {
                    if (item && item.variant_id !== undefined && item.variant_id !== null) {
                        ids.add(`${item.variant_id}`);
                    }
                }
            }
        }

        return Array.from(ids);
    }

    schedulePageValidation(delayMs = 600, variantIds = null) {
        if (Array.isArray(variantIds) && variantIds.length > 0) {
            variantIds.forEach((id) => {
                if (id !== undefined && id !== null && `${id}`.trim() !== '') {
                    this._pendingVariantIds.add(`${id}`);
                }
            });
        } else {
            this._runFullValidation = true;
            this._pendingVariantIds.clear();
        }

        if (this._validationTimer) {
            clearTimeout(this._validationTimer);
        }
        this._validationTimer = setTimeout(() => {
            const targetVariantIds = this._runFullValidation ? null : Array.from(this._pendingVariantIds);
            this._runFullValidation = false;
            this._pendingVariantIds.clear();
            this.initializePageValidation(targetVariantIds);
        }, delayMs);
    }

    // Initialize inventory validation for all add to cart buttons on the page
    async initializePageValidation(targetVariantIds = null) {
        const targetSet = Array.isArray(targetVariantIds) && targetVariantIds.length
            ? new Set(targetVariantIds.map((id) => `${id}`))
            : null;

        const addToCartButtons = document.querySelectorAll(`
            .product-form__submit[type="submit"],
            .add-button[type="submit"],
            .recharge-card__add-btn[type="button"],
            button[onclick*="openDeliveryModal"],
            .add-to-cart-button
        `);

        let sharedInventoryData = null;
        let sharedCartSnapshot = null;

        if (this.isTodaySelected()) {
            const locationId = this.getSelectedLocationId();
            if (locationId) {
                sharedInventoryData = this.getCachedInventoryData(locationId);
                if (sharedInventoryData) {
                    this.inventoryData = sharedInventoryData;
                } else {
                    this.ensureInventoryPrefetch(locationId)
                        .then((data) => {
                            if (data) {
                                this.schedulePageValidation(120);
                            }
                        })
                        .catch(() => {});
                }
                try {
                    sharedCartSnapshot = await this.fetchCartSnapshot(3000);
                } catch (error) {}
            }
        }

        for (const button of addToCartButtons) {
            let variantId = null;

            const form = button.closest('form');
            if (form) {
                const variantInput = form.querySelector('input[name="id"]');
                if (variantInput) {
                    variantId = variantInput.value;
                }
            }

            if (!variantId && button.onclick) {
                const onclickStr = button.getAttribute('onclick') || '';
                const match = onclickStr.match(/openDeliveryModal\('([^']+)'\)/);
                if (match) {
                    variantId = match[1];
                }
            }

            if (!variantId) {
                variantId = button.getAttribute('data-variant-id');
            }

            if (!variantId) {
                continue;
            }

            variantId = `${variantId}`;
            button.dataset.inventoryValidationVariant = variantId;

            if (targetSet && !targetSet.has(variantId)) {
                continue;
            }

            await this.updateButtonState(button, variantId, false, {
                inventoryData: sharedInventoryData,
                cartSnapshot: sharedCartSnapshot,
                button: button,
                productId: (button.closest('[data-product-id]') && button.closest('[data-product-id]').getAttribute('data-product-id')) || null
            });

            if (!button.dataset.inventoryValidationBound) {
                button.dataset.inventoryValidationBound = '1';
                button.addEventListener('click', async (event) => {
                    if (button.disabled || button.getAttribute('aria-disabled') === 'true') {
                        const currentVariantId = button.dataset.inventoryValidationVariant || variantId;
                        const checkResult = await this.canAddToCart(currentVariantId, 1, {
                            inventoryData: this.inventoryData,
                            button: button,
                            productId: (button.closest('[data-product-id]') && button.closest('[data-product-id]').getAttribute('data-product-id')) || null
                        });
                        if (!checkResult.canAdd && checkResult.reason === 'exceeds_inventory') {
                            this.showInventoryWarning(currentVariantId, checkResult);
                        } else if (!checkResult.canAdd && checkResult.reason === 'inventory_prefetch_pending') {
                            this.showInventoryLoadingWarning();
                        }
                        event.preventDefault();
                        event.stopPropagation();
                        return false;
                    }
                });
            }
        }
    }

    }
    window.InventoryValidator = InventoryValidator;
    }
    
    if (!window.inventoryValidator && window.InventoryValidator) {
        window.inventoryValidator = new window.InventoryValidator();
    }

    // Lightweight initial validation so PDP/PLP buttons reflect inventory on first load.
    // Use the existing debounced scheduler to avoid redundant work on large grids.
    (function bootstrapInitialInventoryValidation() {
        if (!window.inventoryValidator) {
            return;
        }
        var schedule = function () {
            try {
                if (!window.inventoryValidator) return;
                // Delay slightly to allow other deferred scripts (product-form, pricing, etc.) to wire up.
                window.inventoryValidator.schedulePageValidation(150);
            } catch (e) {}
        };
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', function onInventoryDomReady() {
                document.removeEventListener('DOMContentLoaded', onInventoryDomReady);
                schedule();
            });
        } else {
            schedule();
        }
    })();

     function shouldRunCommittedFulfillmentChecks() {
         const isCommitted = !!(window.FulfillmentState && typeof window.FulfillmentState.isCommitted === 'function' && window.FulfillmentState.isCommitted());
         const isDraftMode = !!(window.FulfillmentState && typeof window.FulfillmentState.isDraftMode === 'function' && window.FulfillmentState.isDraftMode());
         return isCommitted && !isDraftMode;
     }

     function warmInventoryInBackground(options = {}) {
         const allowDraft = options.allowDraft === true;
         const refreshUi = options.refreshUi !== false;
         if (!window.inventoryValidator) {
             return;
         }
         const isCommittedAndActive = shouldRunCommittedFulfillmentChecks();
         if (!allowDraft && !isCommittedAndActive) {
             return;
         }
         if (!window.inventoryValidator.isTodaySelected()) {
             return;
         }

         const locationId = window.inventoryValidator.getSelectedLocationId();
         if (!locationId) {
             return;
         }

         window.inventoryValidator.ensureInventoryPrefetch(locationId)
             .then((data) => {
                 if (data && refreshUi && isCommittedAndActive) {
                     window.inventoryValidator.schedulePageValidation(100);
                     window.inventoryValidator.checkAndAdjustCartQuantities();
                     if (window.inventoryChecker) {
                         try {
                             window.inventoryChecker.inventoryData = data;
                             if (typeof window.inventoryChecker.updateProductCards === 'function') {
                                 const cartSnapshot = window.inventoryValidator.getCartFromWatcher();
                                 window.inventoryChecker.updateProductCards(cartSnapshot);
                             }
                         } catch (e) {}
                     }
                 }
             })
             .catch(() => {});
     }

    function initInventoryValidatorCartSubscription(attempt = 0) {
        const MAX_RETRIES = 5;
        const currentAttempt = Number.isInteger(attempt) ? attempt : 0;
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
            if (!window.__INVENTORY_VALIDATOR_CART_SUBSCRIBED__) {
                window.__INVENTORY_VALIDATOR_CART_SUBSCRIBED__ = true;

                subscribe(PUB_SUB_EVENTS.cartUpdate, function(event) {
                    if (window.inventoryValidator) {
                        if (!shouldRunCommittedFulfillmentChecks()) {
                            return;
                        }
                        const cartData = event && event.cartData;
                        const hasCartItems = !!(cartData && typeof cartData === 'object' && Array.isArray(cartData.items));
                        if (hasCartItems) {
                            window.inventoryValidator._cartSnapshot = cartData;
                            window.inventoryValidator._cartSnapshotAt = Date.now();
                            if (window.cartWatcher && typeof window.cartWatcher.storeCart === 'function') {
                                try {
                                    window.cartWatcher.storeCart(cartData);
                                } catch (error) {}
                            }
                        }

                        const changedVariantIds = window.inventoryValidator.extractVariantIdsFromEvent(event);
                        const hasSpecificVariants = changedVariantIds.length > 0;
                        const needsFreshCartSync =
                            !hasCartItems &&
                            !!(event && (event.source === 'product-form' || event.productVariantId || event.variantId));

                        if (needsFreshCartSync && window.cartWatcher && typeof window.cartWatcher.emitCartChanges === 'function') {
                            Promise.resolve(window.cartWatcher.emitCartChanges()).catch(() => {});
                        }

                        // Prefer targeted validation to avoid scanning every card and re-fetching /cart.js.
                        window.inventoryValidator.schedulePageValidation(
                            needsFreshCartSync ? (hasSpecificVariants ? 420 : 900) : (hasSpecificVariants ? 220 : 800),
                            hasSpecificVariants ? changedVariantIds : null
                        );
                    }
                });
            }
        } else {
            if (currentAttempt < MAX_RETRIES) {
                setTimeout(() => initInventoryValidatorCartSubscription(currentAttempt + 1), 1000);
            } else {
                console.warn('InventoryValidator: Failed to initialize cart subscription after max retries');
            }
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initInventoryValidatorCartSubscription);
    } else {
        initInventoryValidatorCartSubscription();
    }

    let lastFulfillmentDateHandledAt = 0;
    function handleFulfillmentDateChanged() {
        const now = Date.now();
        if (now - lastFulfillmentDateHandledAt < 100) {
            return;
        }
        lastFulfillmentDateHandledAt = now;

        if (window.inventoryValidator) {
            if (!window.inventoryValidator.isTodaySelected()) {
                window.inventoryValidator.schedulePageValidation(150);
                return;
            }
            warmInventoryInBackground({ allowDraft: true, refreshUi: false });
            if (!shouldRunCommittedFulfillmentChecks()) {
                return;
            }
            warmInventoryInBackground({ allowDraft: false, refreshUi: true });
            setTimeout(() => {
                window.inventoryValidator.checkAndAdjustCartQuantities();
            }, 1000); // Give a moment for the date change to be processed
        }
    }
     // Listen for fulfillment date changes to check and adjust cart quantities (modal may dispatch on document)
     window.addEventListener('fulfillmentDateChanged', handleFulfillmentDateChanged);
     document.addEventListener('fulfillmentDateChanged', handleFulfillmentDateChanged);

     // Also check cart quantities when location changes (debounced like handleFulfillmentDateChanged)
     let lastLocationHandledAt = 0;
     function handleLocationChanged() {
         const now = Date.now();
         if (now - lastLocationHandledAt < 100) return;
         lastLocationHandledAt = now;
         if (window.inventoryValidator) {
             warmInventoryInBackground({ allowDraft: true, refreshUi: false });
             if (!shouldRunCommittedFulfillmentChecks()) {
                 return;
             }
             warmInventoryInBackground({ allowDraft: false, refreshUi: true });
             setTimeout(() => {
                 window.inventoryValidator.checkAndAdjustCartQuantities();
             }, 1000);
         }
     }
     window.addEventListener('locationChanged', handleLocationChanged);

     window.addEventListener('locationPricesReady', function() {
         warmInventoryInBackground({ allowDraft: true, refreshUi: true });
     });

     window.addEventListener('fulfillmentCommitted', function() {
         warmInventoryInBackground({ allowDraft: true, refreshUi: true });
     });

     if (document.readyState === 'loading') {
         document.addEventListener('DOMContentLoaded', function onInventoryValidatorReady() {
             document.removeEventListener('DOMContentLoaded', onInventoryValidatorReady);
             warmInventoryInBackground({ allowDraft: true, refreshUi: true });
         });
     } else {
         warmInventoryInBackground({ allowDraft: true, refreshUi: true });
     }

    // Add CSS animations for notifications (only add once)
    if (!document.getElementById('inventory-validator-styles')) {
        const style = document.createElement('style');
        style.id = 'inventory-validator-styles';
        style.textContent = `
            @keyframes slideInRight {
                from {
                    transform: translateX(100%);
                    opacity: 0;
                }
                to {
                    transform: translateX(0);
                    opacity: 1;
                }
            }
            
            @keyframes slideOutRight {
                from {
                    transform: translateX(0);
                    opacity: 1;
                }
                to {
                    transform: translateX(100%);
                    opacity: 0;
                }
            }
            
            .inventory-disabled {
                opacity: 0.5 !important;
                cursor: not-allowed !important;
            }
        `;
        document.head.appendChild(style);
    }
} // End guard for __INVENTORY_VALIDATOR_LOADED 
